import type { KnjizbaZaPrikaz } from '@koda/shared';
import { useRef, type JSX, type KeyboardEvent, type MouseEvent } from 'react';

import { datum, jeNegativen, znesek } from '../lib/format';
import { useBesedila } from '../lib/jezik';

/** Stolpci seznama knjižb, v vrstnem redu prikaza. */
export const STOLPCI_KNJIZB = ['datum', 'konto', 'dokument', 'partner', 'opis', 'znesek'] as const;
export type StolpecKnjizb = (typeof STOLPCI_KNJIZB)[number];

/** Širine stolpcev v pikslih; `null` = brskalnik jih določi sam. */
export type SirineStolpcev = Record<StolpecKnjizb, number> | null;

/** Najožji stolpec: še vedno ga je mogoče prijeti in razširiti nazaj. */
const NAJOZJI = 40;
/** Korak puščic na tipkovnici. */
const KORAK = 10;
const KLJUC_SHRAMBE = 'koda.razclenitev.sirine';

/**
 * Širine, ki si jih je uporabnik nastavil v tem brskalniku.
 *
 * Shramba je samo udobje: če je ni ali je pokvarjena, velja privzeta
 * postavitev, razčlenitev pa deluje naprej.
 */
export function preberiSirine(): SirineStolpcev {
  try {
    const surovo = window.localStorage.getItem(KLJUC_SHRAMBE);
    if (surovo === null) return null;
    const podatki = JSON.parse(surovo) as Record<string, unknown>;
    const izid = {} as Record<StolpecKnjizb, number>;
    for (const s of STOLPCI_KNJIZB) {
      const v = podatki[s];
      if (typeof v !== 'number' || !Number.isFinite(v)) return null;
      izid[s] = Math.max(NAJOZJI, Math.round(v));
    }
    return izid;
  } catch {
    return null;
  }
}

export function shraniSirine(sirine: SirineStolpcev): void {
  try {
    if (sirine === null) window.localStorage.removeItem(KLJUC_SHRAMBE);
    else window.localStorage.setItem(KLJUC_SHRAMBE, JSON.stringify(sirine));
  } catch {
    // Brskalnik shrambe ne dovoli: širine veljajo samo do zaprtja strani.
  }
}

/** Širina stolpca z ikono izvirne vrstice; ni nastavljiv in ni v shranjenih širinah. */
const SIRINA_IKONE = 34;

interface Lastnosti {
  knjizbe: KnjizbaZaPrikaz[];
  sirine: SirineStolpcev;
  naSirine: (sirine: SirineStolpcev) => void;
  /**
   * Odpre izvirno vrstico knjižbe (samo skrbnik; docs/ARHITEKTURA.md, 5.6).
   * Brez tega stolpca z ikono ni.
   */
  naIzvirnoVrstico?: (knjizba: KnjizbaZaPrikaz) => void;
}

/**
 * Seznam knjižb razčlenitve s stolpci, ki jim uporabnik sam nastavi širino.
 *
 * Kot v Excelu: vlečenje desnega roba glave stolpec razširi ali zoži, dvoklik
 * na rob ga prilagodi vsebini, puščici levo/desno na izbranem robu ga
 * spremenita s tipkovnico. Dokler uporabnik ničesar ne spremeni, širine
 * določi brskalnik; ob prvi spremembi se trenutne širine vseh stolpcev
 * zapišejo, da se drugi stolpci ne premaknejo (docs/ARHITEKTURA.md, 6.2).
 */
export function TabelaKnjizb({ knjizbe, sirine, naSirine, naIzvirnoVrstico }: Lastnosti): JSX.Element {
  const b = useBesedila();
  const tabela = useRef<HTMLTableElement>(null);
  const nazivi: Record<StolpecKnjizb, string> = {
    datum: b.glavaDatum,
    konto: b.glavaKonto,
    dokument: b.glavaDokument,
    partner: b.glavaPartner,
    opis: b.glavaOpis,
    znesek: b.glavaZnesek,
  };

  /** Širine, kot jih stolpci imajo zdaj na zaslonu. */
  const izmerjene = (): Record<StolpecKnjizb, number> => {
    const glave = tabela.current?.querySelectorAll('thead th') ?? [];
    const izid = {} as Record<StolpecKnjizb, number>;
    STOLPCI_KNJIZB.forEach((s, i) => {
      const th = glave[i] as HTMLElement | undefined;
      izid[s] = Math.max(NAJOZJI, Math.round(th?.getBoundingClientRect().width ?? 0));
    });
    return izid;
  };

  /**
   * Vlečenje roba: širina sledi miški, shrani se ob spustu.
   *
   * Samo klik na ročaj (ali premik, ki širine ne spremeni) ni sprememba:
   * širine ostanejo brskalnikove in shramba prazna. Dvoklik je tudi dva
   * klika, zato bi sicer vsak dvoklik najprej zapisal trenutne širine.
   */
  const zacniVlecenje = (stolpec: StolpecKnjizb, d: MouseEvent): void => {
    d.preventDefault();
    const osnova = sirine ?? izmerjene();
    const zacetek = d.clientX;
    let zadnje: SirineStolpcev = null;
    const premik = (e: globalThis.MouseEvent): void => {
      const nova = Math.max(NAJOZJI, Math.round(osnova[stolpec] + e.clientX - zacetek));
      if (zadnje === null && nova === osnova[stolpec]) return;
      zadnje = { ...osnova, [stolpec]: nova };
      naSirine(zadnje);
    };
    const konec = (): void => {
      document.removeEventListener('mousemove', premik);
      document.removeEventListener('mouseup', konec);
      document.body.classList.remove('vlecem-stolpec');
      if (zadnje !== null) shraniSirine(zadnje);
    };
    document.body.classList.add('vlecem-stolpec');
    document.addEventListener('mousemove', premik);
    document.addEventListener('mouseup', konec);
  };

  /** Dvoklik: stolpec je širok kot njegova najširša celica. */
  const prilagodi = (stolpec: StolpecKnjizb): void => {
    const i = STOLPCI_KNJIZB.indexOf(stolpec);
    const celice = tabela.current?.querySelectorAll(
      `tr > :nth-child(${String(i + 1)})`,
    ) ?? [];
    let najvec = NAJOZJI;
    for (const c of celice) najvec = Math.max(najvec, (c as HTMLElement).scrollWidth);
    const nove = { ...(sirine ?? izmerjene()), [stolpec]: najvec };
    naSirine(nove);
    shraniSirine(nove);
  };

  /** Puščici levo/desno na izbranem robu: stolpec ožji ali širši. */
  const naTipko = (stolpec: StolpecKnjizb, d: KeyboardEvent): void => {
    if (d.key !== 'ArrowLeft' && d.key !== 'ArrowRight') return;
    d.preventDefault();
    const osnova = sirine ?? izmerjene();
    const premik = d.key === 'ArrowRight' ? KORAK : -KORAK;
    const nove = { ...osnova, [stolpec]: Math.max(NAJOZJI, osnova[stolpec] + premik) };
    naSirine(nove);
    shraniSirine(nove);
  };

  const zIkono = naIzvirnoVrstico !== undefined;
  const vsota =
    sirine === null ? 0 : STOLPCI_KNJIZB.reduce((s, k) => s + sirine[k], 0) + (zIkono ? SIRINA_IKONE : 0);

  return (
    <table
      ref={tabela}
      className={sirine === null ? 'knjizbe' : 'knjizbe rocne-sirine'}
      style={sirine === null ? undefined : { width: `${String(vsota)}px` }}
    >
      {sirine !== null && (
        <colgroup>
          {STOLPCI_KNJIZB.map((s) => (
            <col key={s} data-stolpec={s} style={{ width: `${String(sirine[s])}px` }} />
          ))}
          {zIkono && <col data-stolpec="izvirna" style={{ width: `${String(SIRINA_IKONE)}px` }} />}
        </colgroup>
      )}
      <thead>
        <tr>
          {STOLPCI_KNJIZB.map((s) => (
            <th key={s} className={s === 'znesek' ? 'stevilka' : undefined}>
              {nazivi[s]}
              <span
                className="rocaj"
                role="separator"
                aria-orientation="vertical"
                aria-label={b.sirinaStolpca(nazivi[s])}
                aria-valuenow={sirine?.[s]}
                tabIndex={0}
                title={b.namigSirine}
                onMouseDown={(d) => {
                  zacniVlecenje(s, d);
                }}
                onDoubleClick={() => {
                  prilagodi(s);
                }}
                onKeyDown={(d) => {
                  naTipko(s, d);
                }}
              />
            </th>
          ))}
          {zIkono && <th className="izvirna-vrstica-glava" aria-label={b.izvirnaVrstica} />}
        </tr>
      </thead>
      <tbody>
        {knjizbe.map((k) => (
          <tr key={k.id}>
            <td>{datum(k.datum)}</td>
            <td>
              <code>{k.konto}</code>
            </td>
            <td>{k.dokument ?? ''}</td>
            <td title={k.partner ?? undefined}>{k.partner ?? ''}</td>
            <td className="opis" title={k.opis ?? undefined}>
              {k.opis ?? ''}
            </td>
            <td className={jeNegativen(k.znesek) ? 'stevilka negativen' : 'stevilka'}>
              {znesek(k.znesek)}
            </td>
            {zIkono && (
              <td className="izvirna-vrstica-celica">
                <button
                  type="button"
                  className="ikona-izvirne-vrstice"
                  title={b.pokaziIzvirnoVrstico}
                  aria-label={`${b.izvirnaVrstica}: ${k.konto}, ${datum(k.datum)}`}
                  onClick={() => {
                    naIzvirnoVrstico(k);
                  }}
                >
                  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
                    <path
                      d="M3 1.5h6.5L13 5v9.5H3z M9.5 1.5V5H13"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinejoin="round"
                    />
                    <path d="M5 8h6M5 10.5h6" stroke="currentColor" strokeWidth="1.2" />
                  </svg>
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
